import React, { useState } from 'react';
import { detectStrategy } from '../../services/factFinderApi';

function StrategyDetectorPanel({ caseData }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleDetect() {
    setLoading(true);
    setError('');
    try {
      const res = await detectStrategy(caseData);
      if (res.success) {
        setResult(res);
      } else {
        setError(res.error || 'Strategy detection failed.');
      }
    } catch (err) {
      setError(err.message || 'Strategy detection failed.');
    } finally {
      setLoading(false);
    }
  }

  const riskColor = {
    low: '#4caf50',
    medium: '#ffc107',
    high: '#f44336',
  };

  return (
    <div className="ff-card ff-p4-panel" data-testid="strategy-detector-panel">
      <div className="ff-p4-header">
        <div>
          <h3>Management Strategy Detector</h3>
          <p className="ff-card-description">
            Analyze what management may be trying to achieve and how to respond.
          </p>
        </div>
        <button
          className="ff-btn-primary ff-btn-sm"
          onClick={handleDetect}
          disabled={loading}
          data-testid="detect-strategy-btn"
        >
          {loading ? 'Analyzing...' : 'Detect Strategy'}
        </button>
      </div>

      {error && <div className="ff-inline-error">{error}</div>}

      {result && (
        <div className="ff-p4-result" data-testid="strategy-result">
          {/* Risk Level */}
          <div
            className="ff-strategy-risk"
            style={{ borderLeftColor: riskColor[result.risk_level] || '#ffc107' }}
            data-testid="risk-level-badge"
          >
            <div className="ff-risk-header">
              <span
                className="ff-risk-dot"
                style={{ background: riskColor[result.risk_level] || '#ffc107' }}
              />
              <span className="ff-risk-label">
                Risk Level: {(result.risk_level || 'medium').toUpperCase()}
              </span>
            </div>
            {result.risk_explanation && (
              <p className="ff-risk-explain">{result.risk_explanation}</p>
            )}
          </div>

          {/* Management Objective */}
          <div className="ff-strategy-section">
            <h4>Possible Management Objective</h4>
            <p>{result.management_objective}</p>
          </div>

          {/* Suggested Response Style */}
          {result.response_style && (
            <div className="ff-strategy-section">
              <h4>Suggested Response Style</h4>
              <p>{result.response_style}</p>
            </div>
          )}

          {/* Pressure Tactics */}
          {result.pressure_tactics?.length > 0 && (
            <div className="ff-strategy-section">
              <h4>Probable Pressure Tactics</h4>
              <div className="ff-tactics-list">
                {result.pressure_tactics.map((t, idx) => (
                  <div key={idx} className="ff-tactic-item" data-testid={`tactic-${idx}`}>
                    <div className="ff-tactic-name">{t.tactic}</div>
                    <p className="ff-tactic-desc">{t.description}</p>
                    <div className="ff-tactic-counter">
                      <strong>Counter:</strong> {t.counter}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Likely Traps */}
          {result.likely_traps?.length > 0 && (
            <div className="ff-strategy-section">
              <h4>Likely Traps</h4>
              <div className="ff-traps-list">
                {result.likely_traps.map((trap, idx) => (
                  <div key={idx} className="ff-trap-item" data-testid={`trap-${idx}`}>
                    <div className="ff-trap-desc">{trap.trap}</div>
                    <div className="ff-trap-danger">
                      <strong>Why dangerous:</strong> {trap.why_dangerous}
                    </div>
                    <div className="ff-trap-safe">
                      <strong>Safe response:</strong> {trap.safe_response}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default StrategyDetectorPanel;
